import { useState } from "react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Ban, ExternalLink, Loader2, MessageSquare, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useTaskMessageAttachments,
  type TaskMessageAttachment,
} from "@/hooks/useTaskMessageAttachments";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface TaskAttachedMessagesSectionProps {
  taskId: string;
  workspaceId: string;
  leadId?: string | null;
  /** Permite remover anexos (ex.: modo edição). */
  canDetach?: boolean;
  /** Quando false, adia o fetch (ex.: diálogo fechado ou subtarefa recolhida). */
  active?: boolean;
  className?: string;
}

function messagePreview(attachment: TaskMessageAttachment): string {
  const msg = attachment.message;
  if (!msg) return "(mensagem indisponível)";
  if (msg.deleted_at) return "Mensagem apagada";

  const body = msg.body?.trim();
  if (body) return body;

  const meta = msg.metadata as { type?: string } | null;
  switch (meta?.type) {
    case "image":
      return "[Imagem]";
    case "audio":
    case "ptt":
      return "[Áudio]";
    case "video":
      return "[Vídeo]";
    case "document":
      return "[Documento]";
    case "sticker":
      return "[Figurinha]";
    default:
      return "(sem texto)";
  }
}

function senderLabel(attachment: TaskMessageAttachment): string {
  const msg = attachment.message;
  if (!msg) return "Desconhecido";
  if (msg.sent_by_member?.display_name) return msg.sent_by_member.display_name;
  return msg.from_user === "assistant" ? "IA" : "Cliente";
}

function AttachedMessageBlock({
  attachment,
  leadId,
  canDetach,
  onDetach,
  detaching,
}: {
  attachment: TaskMessageAttachment;
  leadId?: string | null;
  canDetach?: boolean;
  onDetach: (id: string) => void;
  detaching: string | null;
}) {
  const msg = attachment.message;
  const isDeleted = Boolean(msg?.deleted_at);
  const preview = messagePreview(attachment);
  const attachedAt = attachment.created_at
    ? format(parseISO(attachment.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
    : null;
  const messageAt =
    msg?.created_at &&
    format(parseISO(msg.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });

  return (
    <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />
          <p className="text-xs font-medium text-muted-foreground">Mensagem anexada</p>
        </div>
        {canDetach && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
            disabled={detaching === attachment.id}
            onClick={() => onDetach(attachment.id)}
            aria-label="Remover anexo"
          >
            {detaching === attachment.id ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
          </Button>
        )}
      </div>

      <div className="space-y-1">
        <p className="text-xs text-muted-foreground">
          De: <span className="font-medium text-foreground">{senderLabel(attachment)}</span>
          {messageAt && <span> · {messageAt}</span>}
        </p>
        {isDeleted ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground italic">
            <Ban className="h-3.5 w-3.5 shrink-0" />
            Mensagem apagada
          </div>
        ) : (
          <p className="text-sm whitespace-pre-wrap break-words">{preview}</p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {attachedAt && <span>Anexada em {attachedAt}</span>}
        {attachment.creator?.display_name && (
          <span>por {attachment.creator.display_name}</span>
        )}
        {leadId && (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            asChild
          >
            <a
              href={`/conversations?lead_id=${leadId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="h-3 w-3 mr-1 inline" />
              Ver na conversa
            </a>
          </Button>
        )}
      </div>
    </div>
  );
}

export function TaskAttachedMessagesSection({
  taskId,
  workspaceId,
  leadId,
  canDetach = false,
  active = true,
  className,
}: TaskAttachedMessagesSectionProps) {
  const { attachments, loading, detachMessage } = useTaskMessageAttachments(
    taskId,
    workspaceId,
    { enabled: active },
  );
  const { toast } = useToast();
  const [detaching, setDetaching] = useState<string | null>(null);

  const handleDetach = async (attachmentId: string) => {
    try {
      setDetaching(attachmentId);
      await detachMessage(attachmentId);
      toast({ title: "Anexo removido" });
    } catch (err: unknown) {
      toast({
        title: "Erro ao remover anexo",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setDetaching(null);
    }
  };

  if (!active) return null;

  if (loading) {
    return (
      <div className={cn("flex items-center gap-2 text-sm text-muted-foreground py-2", className)}>
        <Loader2 className="h-4 w-4 animate-spin" />
        Carregando mensagens anexadas...
      </div>
    );
  }

  if (attachments.length === 0) return null;

  return (
    <div className={cn("space-y-3", className)}>
      <p className="text-xs font-medium text-muted-foreground">
        Mensagens anexadas ({attachments.length})
      </p>
      {attachments.map((attachment) => (
        <AttachedMessageBlock
          key={attachment.id}
          attachment={attachment}
          leadId={leadId}
          canDetach={canDetach}
          onDetach={handleDetach}
          detaching={detaching}
        />
      ))}
    </div>
  );
}